import { useState, useEffect, useCallback } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import ResponsiveImage from '~/components/Image/ResponsiveImage'

import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import { MeasureTheItemActivityData, MeasureTheItemProps } from './MeasureTheItem.type'
import { generateMeasureTheItemActivityData } from './MeasureTheItem.util'
import DraggableSlider from './DraggableSlider'

const emptyQuestion = {
  measureElement: {
    alt: '',
    srcKey: '',
  },
  imageHeight: 0,
  imageWidth: 0,
  questionId: '',
  _key: '',
}

function MeasureTheItem({
  actionButtonsFC,
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  questions,
}: MeasureTheItemProps): React.ReactElement {
  const [answer, setAnswer] = useState(0)
  const [input, setInput] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [activityData, setActivityData] = useState<MeasureTheItemActivityData>({
    question: emptyQuestion,
    questionIndex: 0,
  })

  const calculateAnswer = useCallback((elementHeight: number, difficulty: number): number => {
    // Calculate scale of element relative to container to find the answer
    let imgAnswer = (elementHeight / 341) * 100
    if (difficulty === 2) {
      // If difficultylevel is 2, then it the answer should be written as decimeter
      imgAnswer = imgAnswer * 10
    } else if (difficulty === 3) {
      // If difficultylevel is 3 then the answer should be written as meter
      imgAnswer = imgAnswer * 100
    } else if (difficulty === 4) {
      // If difficultylevel is 4 then the answer should be written as millimeter
      imgAnswer = imgAnswer / 10
    }
    return Math.round(imgAnswer)
  }, [])

  useEffect(() => {
    setActivityData(generateMeasureTheItemActivityData(questions, questionHistory))
    const answ = calculateAnswer(activityData.question.imageHeight, difficultyLevel)
    setAnswer(answ)
  }, [activityData.question.imageHeight, calculateAnswer, difficultyLevel, questionHistory, questions])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = answer.toString() === input
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (input !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <div className="flex items-end justify-center relative">
        <DraggableSlider />
        <ResponsiveImage
          alt={activityData.question.measureElement.alt}
          height={activityData.question.imageHeight}
          imageClassName="absolute bottom-0 left-12"
          srcKey={activityData.question.measureElement.srcKey}
          width={activityData.question.imageWidth}
        />
      </div>
      <Input
        aria-label="remember"
        className="w-auto md:w-auto"
        isError={answerState === false}
        key={inputKey}
        maxDigits={5}
        onChange={(val) => setInput(val)}
        onEnter={onEnterPress}
        value={input}
      />
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: input !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setAnswer(0)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default MeasureTheItem
